import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiClient from '../../shared/utils/apiConfig';

const INITIAL_DATA = {
  about_app_ar: '',
  about_app_en: '',
  terms_ar: '',
  terms_en: '',
  face_link: '',
  linked_link: '',
  twitter_link: '',
  kilo_price: '',
  pharmacy_name_ar: '',
  pharmacy_name_en: '',
  pharmacy_img: '',
  pharmacy_img_url: '',
  clinic_name_ar: '',
  clinic_name_en: '',
  clinic_img: '',
  clinic_img_url: '',
};

const normalizeSettingsResponse = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return INITIAL_DATA;
  }

  const source =
    payload.data?.settings ||
    payload.data ||
    payload.result?.settings ||
    payload.result ||
    payload.settings ||
    payload;

  const normalizeUrl = (val) => {
    if (!val) return '';
    const s = String(val);
    if (s === '[]' || s.endsWith('/[]')) return '';
    return s;
  };

  return {
    about_app_ar: source?.about_app_ar ?? '',
    about_app_en: source?.about_app_en ?? '',
    terms_ar: source?.terms_ar ?? '',
    terms_en: source?.terms_en ?? '',
    face_link: source?.face_link ?? '',
    linked_link: source?.linked_link ?? '',
    twitter_link: source?.twitter_link ?? '',
    what_number: source?.what_number !== undefined && source?.what_number !== null ? String(source.what_number) : '',
    phone: source?.phone !== undefined && source?.phone !== null ? String(source.phone) : '',
    kilo_price:
      source?.kilo_price !== undefined && source?.kilo_price !== null
        ? String(source.kilo_price)
        : '',
    pharmacy_name_ar: source?.pharmacy_name_ar ?? '',
    pharmacy_name_en: source?.pharmacy_name_en ?? '',
    pharmacy_img: Array.isArray(source?.pharmacy_img) ? '' : (source?.pharmacy_img ?? ''),
    pharmacy_img_url: normalizeUrl(source?.pharmacy_img_url),
    clinic_name_ar: source?.clinic_name_ar ?? '',
    clinic_name_en: source?.clinic_name_en ?? '',
    clinic_img: Array.isArray(source?.clinic_img) ? '' : (source?.clinic_img ?? ''),
    clinic_img_url: normalizeUrl(source?.clinic_img_url),
  };
};

export const fetchAdminAppSettings = createAsyncThunk(
  'adminAppSettings/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/admin_app_settings');
      return normalizeSettingsResponse(response?.data ?? response);
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'فشل في جلب إعدادات التطبيق';
      return rejectWithValue(message);
    }
  }
);

const formatPayloadForState = (payload = {}) => {
  const formatted = {};
  Object.entries(payload).forEach(([key, value]) => {
    if (key === 'kilo_price') {
      formatted[key] =
        value === null || value === undefined || value === ''
          ? ''
          : String(value);
    } else if (key === 'pharmacy_img' || key === 'clinic_img') {
      // لا نخزن ملف File في الستور، فقط إشارة نصية
      formatted[key] = typeof value === 'string' ? value : '';
    } else {
      formatted[key] = value ?? '';
    }
  });
  return formatted;
};

export const updateAdminAppSettings = createAsyncThunk(
  'adminAppSettings/update',
  async (payload, { rejectWithValue }) => {
    try {
      if (payload instanceof FormData) {
        const response = await apiClient.post('/update_admin_app_settings', payload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        return normalizeSettingsResponse(response?.data ?? response);
      } else {
        const response = await apiClient.post('/update_admin_app_settings', payload);
        return normalizeSettingsResponse(response?.data ?? response);
      }
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || 'فشل في تحديث إعدادات التطبيق';
      return rejectWithValue(message);
    }
  }
);

const adminAppSettingsSlice = createSlice({
  name: 'adminAppSettings',
  initialState: {
    data: INITIAL_DATA,
    loading: false,
    error: null,
    saving: false,
    saveError: null,
    lastUpdated: null,
  },
  reducers: {
    resetAdminAppSettingsErrors: (state) => {
      state.error = null;
      state.saveError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminAppSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminAppSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.lastUpdated = Date.now();
      })
      .addCase(fetchAdminAppSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'فشل في جلب إعدادات التطبيق';
      })
      .addCase(updateAdminAppSettings.pending, (state) => {
        state.saving = true;
        state.saveError = null;
      })
      .addCase(updateAdminAppSettings.fulfilled, (state, action) => {
        state.saving = false;
        // بعد الحفظ، خزّن الداتا الجديدة كما هي بدون دمج مع القديم
        state.data = action.payload;
        state.lastUpdated = Date.now();
      })
      .addCase(updateAdminAppSettings.rejected, (state, action) => {
        state.saving = false;
        state.saveError = action.payload || 'فشل في تحديث إعدادات التطبيق';
      });
  },
});

export const { resetAdminAppSettingsErrors } = adminAppSettingsSlice.actions;
export default adminAppSettingsSlice.reducer;

